import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Avatar from "@mui/material/Avatar";
import SendIcon from "@mui/icons-material/Send";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { apiFetch } from "utils/api";

const CHAT_BG_OFFWHITE = "#f7f3ec";
const CHAT_BUBBLE_RECEIVER = "#081b33";
const CHAT_BUBBLE_SENDER = "#144366ff";

function safeParse(raw) {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function fmtTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
}

function fmtDateTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("de-DE");
}

function resolveAssetUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  if (typeof window === "undefined") return url;
  return `${window.location.origin}${url}`;
}

export default function MessengerPage() {
  const profile = useMemo(() => safeParse(localStorage.getItem("solvit_profile") || ""), []);
  const isAdmin = (profile?.role || "").toLowerCase() === "admin";

  const [conversations, setConversations] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [messages, setMessages] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState("");
  const pollRef = useRef(null);
  const messagesBottomRef = useRef(null);

  const selectedConversation = useMemo(
    () => conversations.find((c) => c.customerId === selectedCustomerId) || null,
    [conversations, selectedCustomerId]
  );

  const loadConversations = useCallback(async () => {
    if (!isAdmin) return;
    setLoadingConversations(true);
    try {
      const data = await apiFetch("/api/messages/conversations");
      const list = Array.isArray(data) ? data : data?.conversations || [];
      setConversations(list);
      setSelectedCustomerId((current) => current || list[0]?.customerId || "");
    } catch (e) {
      setError(e?.message || "Konversationen konnten nicht geladen werden.");
    } finally {
      setLoadingConversations(false);
    }
  }, [isAdmin]);

  const loadMessages = useCallback(async () => {
    const hasTarget = isAdmin ? selectedCustomerId : true;
    if (!hasTarget) {
      setMessages([]);
      return;
    }

    setLoadingMessages(true);
    try {
      const q = isAdmin ? `?customerId=${encodeURIComponent(selectedCustomerId)}&limit=300` : "?limit=300";
      const data = await apiFetch(`/api/messages${q}`);
      const list = Array.isArray(data) ? data : data?.messages || [];
      setMessages(list);
    } catch (e) {
      setError(e?.message || "Nachrichten konnten nicht geladen werden.");
    } finally {
      setLoadingMessages(false);
    }
  }, [isAdmin, selectedCustomerId]);

  const markMessagesRead = useCallback(async () => {
    try {
      await apiFetch("/api/messages/read", {
        method: "POST",
        body: isAdmin ? { customerId: selectedCustomerId || undefined } : {},
      });
      window.dispatchEvent(new Event("solvit-chat-read"));
    } catch {
      // ignore optimistic read sync errors
    }
  }, [isAdmin, selectedCustomerId]);

  const sendMessage = useCallback(async () => {
    const text = draft.trim();
    if (!text) return;

    const payload = isAdmin ? { customerId: selectedCustomerId, text } : { text };
    if (isAdmin && !selectedCustomerId) return;

    setSending(true);
    setError("");
    try {
      await apiFetch("/api/messages", { method: "POST", body: payload });
      setDraft("");
      await loadMessages();
      if (isAdmin) await loadConversations();
    } catch (e) {
      setError(e?.message || "Nachricht konnte nicht gesendet werden.");
    } finally {
      setSending(false);
    }
  }, [draft, isAdmin, selectedCustomerId, loadMessages, loadConversations]);

  useEffect(() => {
    if (isAdmin) {
      void loadConversations();
    }
  }, [isAdmin, loadConversations]);

  useEffect(() => {
    void loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    const hasTarget = isAdmin ? !!selectedCustomerId : true;
    if (!hasTarget) return;
    void markMessagesRead();
  }, [isAdmin, selectedCustomerId, markMessagesRead]);

  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      void loadMessages();
      if (isAdmin) void loadConversations();
    }, 5000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [isAdmin, loadMessages, loadConversations]);

  useEffect(() => {
    messagesBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDTypography variant="h5" fontWeight="medium" mb={2}>
          Messenger
        </MDTypography>

        {!!error && (
          <MDBox mb={2}>
            <MDTypography variant="button" color="error">
              {error}
            </MDTypography>
          </MDBox>
        )}

        <Card>
          <Grid container sx={{ minHeight: "70vh", maxHeight: "70vh" }}>
            {isAdmin && (
              <Grid item xs={12} md={4} sx={{ borderRight: "1px solid rgba(0,0,0,0.12)", overflowY: "auto" }}>
                <MDBox p={2}>
                  <MDTypography variant="button" color="text">
                    Kunden
                  </MDTypography>
                </MDBox>
                <Divider />
                {loadingConversations ? (
                  <MDBox p={3} textAlign="center">
                    <CircularProgress size={24} />
                  </MDBox>
                ) : (
                  <List disablePadding>
                    {conversations.map((conv) => (
                      <ListItemButton
                        key={conv.customerId}
                        selected={selectedCustomerId === conv.customerId}
                        onClick={() => setSelectedCustomerId(conv.customerId)}
                        sx={{ py: 0.5, px: 1.5 }}
                      >
                        <ListItemText
                          primary={conv.companyName}
                          primaryTypographyProps={{ fontSize: "0.8rem", fontWeight: 500 }}
                          secondaryTypographyProps={{ fontSize: "0.72rem", noWrap: true }}
                          secondary={
                            conv.lastMessage
                              ? `${conv.lastMessage.senderRole === "CUSTOMER" ? "" : "Support: "}${conv.lastMessage.text}`
                              : "Noch keine Nachrichten"
                          }
                        />
                      </ListItemButton>
                    ))}
                  </List>
                )}
              </Grid>
            )}

            <Grid item xs={12} md={isAdmin ? 8 : 12} sx={{ display: "flex", flexDirection: "column" }}>
              <MDBox px={2} py={1.5} sx={{ borderBottom: "1px solid rgba(0,0,0,0.12)" }}>
                <MDTypography variant="button" fontWeight="medium">
                  {isAdmin
                    ? selectedConversation
                      ? selectedConversation.companyName
                      : "Kunde auswählen"
                    : "Support"}
                </MDTypography>
              </MDBox>

              <MDBox sx={{ flex: 1, overflowY: "auto", bgcolor: CHAT_BG_OFFWHITE, p: 2 }}>
                {loadingMessages ? (
                  <MDBox py={3} textAlign="center">
                    <CircularProgress size={24} />
                  </MDBox>
                ) : messages.length === 0 ? (
                  <MDTypography variant="button" color="text">
                    Noch keine Nachrichten.
                  </MDTypography>
                ) : (
                  messages.map((msg) => {
                    const mine = msg.senderRole === (isAdmin ? "ADMIN" : "CUSTOMER");
                    return (
                      <MDBox key={msg.id} mb={1.5} display="flex" justifyContent={mine ? "flex-end" : "flex-start"}>
                        {!mine && (
                          <Avatar
                            src={resolveAssetUrl(msg.senderAvatarUrl)}
                            alt={msg.senderDisplayName || "Kontakt"}
                            sx={{ width: 32, height: 32, alignSelf: "flex-end", mr: 1 }}
                          />
                        )}
                        <MDBox
                          sx={{
                            maxWidth: "75%",
                            px: 1.5,
                            py: 1,
                            borderRadius: 2,
                            bgcolor: mine ? CHAT_BUBBLE_SENDER : CHAT_BUBBLE_RECEIVER,
                            boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
                          }}
                        >
                          <MDTypography variant="button" sx={{ color: "#ffffff" }}>
                            {msg.text}
                          </MDTypography>
                          <MDTypography
                            variant="caption"
                            display="block"
                            sx={{ color: "rgba(255,255,255,0.82)", textAlign: "right", mt: 0.5 }}
                          >
                            {fmtTime(msg.createdAt)}
                          </MDTypography>
                        </MDBox>
                        {mine && (
                          <Avatar
                            src={resolveAssetUrl(msg.senderAvatarUrl || profile?.avatarUrl)}
                            alt={msg.senderDisplayName || "Ich"}
                            sx={{ width: 32, height: 32, alignSelf: "flex-end", ml: 1 }}
                          />
                        )}
                      </MDBox>
                    );
                  })
                )}
                <div ref={messagesBottomRef} />
              </MDBox>

              <MDBox
                sx={{ borderTop: "1px solid rgba(0,0,0,0.12)", p: 1.5, bgcolor: "white", display: "flex", gap: 1 }}
              >
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Nachricht eingeben..."
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      void sendMessage();
                    }
                  }}
                  disabled={sending || (isAdmin && !selectedCustomerId)}
                />
                <IconButton
                  color="primary"
                  onClick={() => {
                    void sendMessage();
                  }}
                  disabled={sending || !draft.trim() || (isAdmin && !selectedCustomerId)}
                >
                  <SendIcon />
                </IconButton>
              </MDBox>

              <MDBox px={2} pb={1}>
                <MDTypography variant="caption" color="text">
                  Letzte Aktualisierung: {fmtDateTime(messages[messages.length - 1]?.createdAt)}
                </MDTypography>
              </MDBox>
            </Grid>
          </Grid>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
